import * as React from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { Search } from 'lucide-react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Alert } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { FilterDropdown } from '@/components/ui/filter-dropdown'
import { Input } from '@/components/ui/input'
import {
  SortableTableHead,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { getJSON } from '@/lib/api'
import { displayDate } from '@/lib/dates'
import { MemberPortalOpenInvoices, type OpenInvoice } from './MemberPortalOpenInvoices'
import type { MemberPortalOrganization } from './MemberSignIn'
import { MemberPortalTransactions } from './MemberPortalTransactions'
import { organizationSection } from './portalOrganizationSections'
import { usePortalViewer } from './usePortalViewer'

interface PortalInvoice {
  id: number
  number: number
  origin: string
  status: string
}

interface HistoricalInvoice extends PortalInvoice {
  issue_on: string
  total: string
}

interface PortalPayment {
  id: string
  paid_on: string
  method: string
  applied_to: string[]
  amount: string
}

interface BillingResponse {
  billing: {
    currency: string
    open_invoices: OpenInvoice[]
    history: HistoricalInvoice[]
    payments: PortalPayment[]
    account_credit: string | null
    organization_billings: OrganizationBilling[]
  }
}

interface OrganizationBilling {
  organization_id: number
  organization_name: string
  open_invoices: OpenInvoice[]
}

const statusOptions = [
  { value: 'paid', label: 'Paid' },
  { value: 'voided', label: 'Voided' },
]
const originOptions = [
  { value: 'renewal', label: 'Renewal' },
  { value: 'application', label: 'Application' },
  { value: 'level_change', label: 'Level change' },
  { value: 'manual', label: 'Manual' },
]

export function MemberPortalBilling({
  organizations = [],
}: {
  organizations?: MemberPortalOrganization[]
}) {
  const navigate = useNavigate()
  const viewer = usePortalViewer()
  const location = useLocation()
  const selectedFromOverview = selectedInvoiceIds(location.search)
  const [search, setSearch] = React.useState('')
  const [statuses, setStatuses] = React.useState<Set<string>>(new Set())
  const [origins, setOrigins] = React.useState<Set<string>>(new Set())
  const [sort, setSort] = React.useState<'newest' | 'oldest'>('newest')
  const queryString = billingQueryString({ search, statuses, origins, sort })
  const billing = useQuery<BillingResponse>({
    queryKey: ['member-portal-billing', queryString],
    queryFn: () => getJSON(`/api/v1/member_portal/billing${queryString}`),
    placeholderData: keepPreviousData,
    retry: false,
  })

  if (billing.isLoading) {
    return (
      <p className="mt-4 text-body-sm text-muted-foreground" role="status">
        Loading Billing...
      </p>
    )
  }
  if (billing.isError || !billing.data) {
    return <Alert className="mt-4">We couldn’t load your Billing history.</Alert>
  }

  const data = billing.data.billing
  return (
    <div className="mt-4 space-y-6">
      {data.open_invoices.length > 0 && (
        <MemberPortalOpenInvoices
          invoices={data.open_invoices}
          initiallySelectedIds={selectedFromOverview}
          onOpen={(invoice) => navigateToInvoice(invoice)}
        />
      )}
      {data.account_credit && <AccountCredit amount={data.account_credit} />}
      {data.organization_billings.map((organization) => (
        <MemberPortalOpenInvoices
          key={organization.organization_id}
          heading={organization.organization_name}
          description="You are the billing contact."
          invoices={organization.open_invoices}
          secondaryAction={data.open_invoices.length > 0}
          onOpen={(invoice) => navigateToInvoice(invoice)}
        />
      ))}
      <CompanyInvoices
        billings={data.organization_billings}
        organizations={organizations}
        viewer={viewer}
      />
      <History
        invoices={data.history}
        search={search}
        statuses={statuses}
        origins={origins}
        sort={sort}
        onSearchChange={setSearch}
        onStatusesChange={setStatuses}
        onOriginsChange={setOrigins}
        onSortChange={setSort}
        onOpen={(invoice) => navigateToInvoice(invoice)}
      />
      <Payments payments={data.payments} />
    </div>
  )

  function navigateToInvoice(invoice: PortalInvoice) {
    navigate(`/portal/billing/invoices/${invoice.id}`)
  }
}

// A Billing contact sees each organization's invoices when its Organization dashboard is open to them.
function CompanyInvoices({
  billings,
  organizations,
  viewer,
}: {
  billings: OrganizationBilling[]
  organizations: MemberPortalOrganization[]
  viewer: string
}) {
  const visible = billings.filter((billing) =>
    organizations.some(
      (organization) =>
        organization.id === billing.organization_id &&
        organizationSection(organization, 'dashboard'),
    ),
  )
  return visible.map((billing) => (
    <MemberPortalTransactions
      key={billing.organization_id}
      organizationId={billing.organization_id}
      viewer={viewer}
      title={`${billing.organization_name} invoices`}
      redirectWhenMissing={false}
    />
  ))
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return <h2 className="text-body font-semibold text-foreground">{children}</h2>
}

function selectedInvoiceIds(search: string) {
  return new URLSearchParams(search).get('select')?.split(',').map(Number).filter(Number.isInteger)
}

function AccountCredit({ amount }: { amount: string }) {
  return (
    <section className="space-y-3">
      <SectionHeading>Account credit</SectionHeading>
      <div className="overflow-hidden rounded-md border border-border bg-card">
        <div className="flex items-center justify-between gap-4 px-4 py-3">
          <span className="text-body-sm text-muted-foreground">Available credit</span>
          <span className="text-right text-body-sm font-medium tabular-nums">{amount}</span>
        </div>
        <p className="border-t border-border px-4 py-2 text-caption text-muted-foreground">
          The association applies credit to open invoices.
        </p>
      </div>
    </section>
  )
}

interface HistoryProps {
  invoices: HistoricalInvoice[]
  search: string
  statuses: Set<string>
  origins: Set<string>
  sort: 'newest' | 'oldest'
  onSearchChange: (value: string) => void
  onStatusesChange: (value: Set<string>) => void
  onOriginsChange: (value: Set<string>) => void
  onSortChange: (value: 'newest' | 'oldest') => void
  onOpen: (invoice: HistoricalInvoice) => void
}

function History(props: HistoryProps) {
  const {
    invoices,
    search,
    statuses,
    origins,
    sort,
    onSearchChange,
    onStatusesChange,
    onOriginsChange,
    onSortChange,
    onOpen,
  } = props
  return (
    <section className="space-y-3">
      <SectionHeading>History</SectionHeading>
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-full max-w-xs">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="Search invoices"
            className="h-9 pl-9"
          />
        </div>
        <FilterDropdown
          label="Status"
          emptyLabel="All statuses"
          options={statusOptions}
          selected={statuses}
          onChange={onStatusesChange}
        />
        <FilterDropdown
          label="Origin"
          emptyLabel="All origins"
          options={originOptions}
          selected={origins}
          onChange={onOriginsChange}
        />
      </div>
      <div className="overflow-hidden rounded-md border border-border">
        <Table className="min-w-[600px]">
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <SortableTableHead
                active
                direction={sort === 'oldest' ? 'ascending' : 'descending'}
                onToggle={() => onSortChange(sort === 'newest' ? 'oldest' : 'newest')}
              >
                Date
              </SortableTableHead>
              <TableHead>Invoice</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Amount</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {invoices.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center text-muted-foreground">
                  No Invoice history yet.
                </TableCell>
              </TableRow>
            ) : (
              invoices.map((invoice) => (
                <HistoryRow key={invoice.id} invoice={invoice} onOpen={() => onOpen(invoice)} />
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </section>
  )
}

function HistoryRow({ invoice, onOpen }: { invoice: HistoricalInvoice; onOpen: () => void }) {
  const isVoided = invoice.status === 'Voided'
  const muted = isVoided ? 'text-muted-foreground' : ''
  return (
    <TableRow
      tabIndex={0}
      aria-label={`Open Invoice ${invoice.number}`}
      onClick={onOpen}
      onKeyDown={(event) => openRowFromKeyboard(event, onOpen)}
      className="cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
    >
      <TableCell className={muted}>{displayDate(invoice.issue_on)}</TableCell>
      <TableCell className={`font-medium ${muted}`}>
        {invoice.number} · {invoice.origin}
      </TableCell>
      <TableCell>
        <Badge variant={isVoided ? 'neutral' : 'success'}>{invoice.status}</Badge>
      </TableCell>
      <TableCell className={`text-right tabular-nums ${muted}`}>{invoice.total}</TableCell>
    </TableRow>
  )
}

function openRowFromKeyboard(event: React.KeyboardEvent, onOpen: () => void) {
  if (event.key !== 'Enter' && event.key !== ' ') return

  event.preventDefault()
  onOpen()
}

function Payments({ payments }: { payments: PortalPayment[] }) {
  return (
    <section className="space-y-3">
      <SectionHeading>Payments</SectionHeading>
      <div className="overflow-hidden rounded-md border border-border">
        <Table className="min-w-[600px]">
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Date</TableHead>
              <TableHead>Method</TableHead>
              <TableHead>Applied to</TableHead>
              <TableHead className="text-right">Amount</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {payments.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center text-muted-foreground">
                  No Payments yet.
                </TableCell>
              </TableRow>
            ) : (
              payments.map((payment) => (
                <TableRow key={payment.id}>
                  <TableCell>{displayDate(payment.paid_on)}</TableCell>
                  <TableCell>{payment.method}</TableCell>
                  <TableCell>{payment.applied_to.join(', ') || '—'}</TableCell>
                  <TableCell className="text-right tabular-nums">{payment.amount}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </section>
  )
}

function billingQueryString({
  search,
  statuses,
  origins,
  sort,
}: {
  search: string
  statuses: Set<string>
  origins: Set<string>
  sort: 'newest' | 'oldest'
}) {
  const params = new URLSearchParams()
  if (search.trim()) params.set('search', search.trim())
  if (statuses.size > 0) params.set('status', Array.from(statuses).sort().join(','))
  if (origins.size > 0) params.set('origin', Array.from(origins).sort().join(','))
  params.set('sort', sort)
  return `?${params.toString()}`
}
